import React from 'react';
import { SectionTitleV2 } from '../../../atoms/v2/section-title/section-title.atom';
import * as S from '../checkout-form/checkout-form.organism.style';
import { InputField } from '../../../molecules/input-field/input-field.molecule';
import { useTranslation } from 'react-i18next';
import { FormDataShape } from '../../../../common/hooks/useCheckoutController/useCheckoutController.types';
import { TbTruckDelivery } from 'react-icons/tb';
import { Checkout } from '../../../../common/types/checkout';
import { LabelV2 } from '../../../atoms/v2/label/label-v2.atom';
import { ZipCodeInput } from '../../../molecules/classic/zip-code-input/zip-code-input.molecule';
import { InputContainer } from '../../../atoms/v2/input-container/input-container.atom';

type Props = {
  checkout: Checkout;
  formData: FormDataShape;
  setFormData: (field: keyof FormDataShape, value: string) => void;
};

export const DeliveryForm: React.FC<Props> = ({
  formData,
  setFormData,
  checkout,
}) => {
  const { t } = useTranslation();

  const INVALID_TEXT_REGEX = /[^a-zA-ZÀ-ÿ\s]/g;
  const INVALID_ADDRESS_REGEX = /[^a-zA-ZÀ-ÿ0-9\s.,\-\/ºª#]/g;
  const INVALID_STREET_NUMBER_REGEX = /[^0-9a-zA-Z\s\-\/]/g;
  const MAX_TEXT_LENGTH = 50;

  const sanitizeAddress = (value: string) =>
    value.replace(INVALID_ADDRESS_REGEX, '').slice(0, MAX_TEXT_LENGTH);

  const sanitizeName = (value: string) =>
    value.replace(INVALID_TEXT_REGEX, '').slice(0, MAX_TEXT_LENGTH);

  const sanitizeStreetNumber = (value: string, max = 10) =>
    value.replace(INVALID_STREET_NUMBER_REGEX, '').slice(0, max);

  return (
    <S.FormContainer>
      <SectionTitleV2>
        <TbTruckDelivery size={22} />
        <span>{t('delivery')}</span>
      </SectionTitleV2>

      <S.FormFields>
        <InputContainer>
          <LabelV2 htmlFor="postalCode">{t('postalCode')}</LabelV2>
          <ZipCodeInput
            color={
              checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                ?.background
            }
            id="postalCode"
            country={checkout?.paisEnum}
            value={formData.postalCode}
            onChange={(value) => setFormData('postalCode', value)}
          />
        </InputContainer>

        <InputField
          color={
            checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
              ?.background
          }
          id="state"
          label={t('state')}
          type="text"
          placeholder={t('fillYourState')}
          value={formData.state}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData('state', sanitizeName(e.target.value))
          }
          suppressAnalytics={true}
        />

        <InputField
          color={
            checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
              ?.background
          }
          id="city"
          label={t('city')}
          type="text"
          placeholder={t('fillYourCity')}
          value={formData.city}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData('city', sanitizeName(e.target.value))
          }
          suppressAnalytics={true}
        />

        <InputField
          color={
            checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
              ?.background
          }
          id="neighborhood"
          label={t('district')}
          type="text"
          placeholder={t('fillYourNeighborhood')}
          value={formData.neighborhood}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData('neighborhood', sanitizeAddress(e.target.value))
          }
          suppressAnalytics={true}
        />

        <InputField
          color={
            checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
              ?.background
          }
          id="address"
          label={t('address')}
          type="text"
          placeholder={t('fillYourAddress')}
          value={formData.address}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData('address', sanitizeAddress(e.target.value))
          }
          suppressAnalytics={true}
        />

        <InputField
          color={
            checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
              ?.background
          }
          id="number"
          label={t('number')}
          type="text"
          placeholder={t('fillYourHouseNumber')}
          value={formData.number}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData('number', sanitizeStreetNumber(e.target.value))
          }
          suppressAnalytics={true}
        />

        <InputField
          color={
            checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
              ?.background
          }
          id="complement"
          label={t('complement')}
          type="text"
          placeholder={t('fillYourAddressComplement')}
          value={formData.complement}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData('complement', sanitizeAddress(e.target.value))
          }
        />
      </S.FormFields>
    </S.FormContainer>
  );
};
